import {
  addLocalDays,
  formatDate,
  parseLocalDate,
} from "../requests/utilities/helpers";

const DEFAULT_OPEN_TIME = 9;
const DEFAULT_CLOSE_TIME = 17;

function getStandardHoursForDow(standardOperatingHours, dow) {
  return standardOperatingHours?.find((day) => day.dow === dow);
}

function hasOpenHours(day) {
  return day && (day.open_time !== 0 || day.close_time !== 0);
}

export function getFutureRecurringHoursDefaults(standardOperatingHours, dow) {
  const matchingStandardHours = getStandardHoursForDow(
    standardOperatingHours,
    dow
  );
  const fallbackStandardHours = standardOperatingHours?.[0] || {};

  return {
    open_time:
      matchingStandardHours?.open_time ??
      fallbackStandardHours.open_time ??
      DEFAULT_OPEN_TIME,
    close_time:
      matchingStandardHours?.close_time ??
      fallbackStandardHours.close_time ??
      DEFAULT_CLOSE_TIME,
  };
}

export function buildFutureRecurringHoursRow({
  storeId,
  effectiveStartDate,
  dow,
  standardOperatingHours,
  defaultHours,
}) {
  const resolvedDefaultHours =
    defaultHours || getFutureRecurringHoursDefaults(standardOperatingHours, dow);

  return {
    store: storeId,
    dow,
    effective_start_date: effectiveStartDate,
    open_time: resolvedDefaultHours.open_time,
    close_time: resolvedDefaultHours.close_time,
  };
}

export function getInheritedFutureRecurringHoursDefaults({
  futureRecurringHours,
  effectiveStartDate,
  dow,
  standardOperatingHours,
}) {
  const inheritedRow = (futureRecurringHours || []).reduce(
    (latestMatchingRow, row) => {
      if (
        row?.dow !== dow ||
        !row?.effective_start_date ||
        row.effective_start_date >= effectiveStartDate
      ) {
        return latestMatchingRow;
      }

      if (
        !latestMatchingRow ||
        row.effective_start_date > latestMatchingRow.effective_start_date
      ) {
        return row;
      }

      return latestMatchingRow;
    },
    null
  );

  if (inheritedRow) {
    return {
      open_time: inheritedRow.open_time,
      close_time: inheritedRow.close_time,
    };
  }

  return getFutureRecurringHoursDefaults(standardOperatingHours, dow);
}

export function hasFutureRecurringHoursOpen(row) {
  return row && (row.open_time !== 0 || row.close_time !== 0);
}

export function getFutureRecurringHoursToggleOnDefaults({
  futureRecurringHours,
  effectiveStartDate,
  dow,
  standardOperatingHours,
}) {
  const inheritedHours = getInheritedFutureRecurringHoursDefaults({
    futureRecurringHours,
    effectiveStartDate,
    dow,
    standardOperatingHours,
  });
  if (hasFutureRecurringHoursOpen(inheritedHours)) {
    return inheritedHours;
  }

  const matchingStandardHours = getStandardHoursForDow(
    standardOperatingHours,
    dow
  );
  if (hasOpenHours(matchingStandardHours)) {
    return matchingStandardHours;
  }

  const fallbackOpenStandardHours = standardOperatingHours?.find((day) =>
    hasOpenHours(day)
  );
  if (fallbackOpenStandardHours) {
    return {
      open_time: fallbackOpenStandardHours.open_time,
      close_time: fallbackOpenStandardHours.close_time,
    };
  }

  return {
    open_time: DEFAULT_OPEN_TIME,
    close_time: DEFAULT_CLOSE_TIME,
  };
}

export function buildInheritedFutureRecurringHoursRow({
  futureRecurringHours,
  storeId,
  effectiveStartDate,
  dow,
  standardOperatingHours,
}) {
  return buildFutureRecurringHoursRow({
    storeId,
    effectiveStartDate,
    dow,
    standardOperatingHours,
    defaultHours: getInheritedFutureRecurringHoursDefaults({
      futureRecurringHours,
      effectiveStartDate,
      dow,
      standardOperatingHours,
    }),
  });
}

export function normalizeFutureRecurringHours({
  futureRecurringHours,
  storeId,
}) {
  return (futureRecurringHours || [])
    .filter((row) => row?.effective_start_date && row?.dow)
    .map((row) => ({
      ...row,
      store: row.store ?? storeId,
    }))
    .sort((leftRow, rightRow) => {
      if (leftRow.effective_start_date !== rightRow.effective_start_date) {
        return leftRow.effective_start_date.localeCompare(
          rightRow.effective_start_date
        );
      }
      return leftRow.dow - rightRow.dow;
    });
}

export function groupFutureRecurringHours(futureRecurringHours) {
  const groupedRows = new Map();

  (futureRecurringHours || []).forEach((row) => {
    if (!row?.effective_start_date) {
      return;
    }

    if (!groupedRows.has(row.effective_start_date)) {
      groupedRows.set(row.effective_start_date, []);
    }

    groupedRows.get(row.effective_start_date).push(row);
  });

  return Array.from(groupedRows.entries())
    .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
    .map(([effectiveStartDate, rows]) => ({
      effective_start_date: effectiveStartDate,
      rows: rows.slice().sort((leftRow, rightRow) => leftRow.dow - rightRow.dow),
    }));
}

export function getNextAvailableEffectiveStartDate(existingEffectiveDates) {
  const datesInUse = new Set(existingEffectiveDates || []);
  let candidateDate = parseLocalDate(new Date());

  for (let i = 0; i < 3650; i += 1) {
    const formattedDate = formatDate(candidateDate);
    if (!datesInUse.has(formattedDate)) {
      return formattedDate;
    }

    candidateDate = addLocalDays(candidateDate, 1);
  }

  return formatDate(candidateDate);
}

export function normalizeHoursOfOperationData(location) {
  if (!location) {
    return location;
  }

  return {
    ...location,
    specialHours: location.specialHours || [],
    futureRecurringHours: normalizeFutureRecurringHours({
      futureRecurringHours: location.futureRecurringHours || [],
      storeId: location.storeId,
    }),
  };
}
